// chartSnapshot.js - Shared SVG -> PNG snapshot logic for chart share buttons.
// Extracted from BubbleChart.jsx / ActivityBubbleChart.jsx, which previously
// duplicated this exact canvas-compositing logic.
import { API_BASE } from '../config/api';
import { authService } from '../services/auth';
import logger from './logger';

/**
 * Renders an SVG element (containing <image> children with circular
 * clip-paths, as produced by BubbleChart/ActivityBubbleChart) to a PNG File.
 *
 * Images are pre-loaded and drawn onto the canvas separately from the SVG
 * background because browsers refuse to rasterize an <svg> containing
 * cross-origin <image> references via the Image()->canvas path.
 *
 * @param {SVGSVGElement} svgElement - The source <svg> to snapshot.
 * @param {Object} options
 * @param {number} options.width - Logical width of the svg (unscaled).
 * @param {number} options.height - Logical height of the svg (unscaled).
 * @param {Object} [options.cropBounds] - Optional {x, y, width, height} crop.
 * @param {string} [options.filename] - Filename for the resulting File.
 * @param {string} [options.backgroundColor] - Canvas background fill.
 * @param {number} [options.scale] - Pixel-density multiplier (default 2).
 * @returns {Promise<File|null>} Resolves with a PNG File, or null if the
 *   svg could not be rasterized.
 */
function utf8ToBase64(str) {
  // eslint-disable-next-line no-undef
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i]);
  }
  // eslint-disable-next-line no-undef
  return btoa(bin);
}

export function svgElementToPngFile(svgElement, {
  width,
  height,
  cropBounds,
  filename = 'chart-snapshot.png',
  backgroundColor,
  scale = 2,
} = {}) {
  return new Promise((resolve) => {
    if (!svgElement || !width || !height) {
      resolve(null);
      return;
    }

    const cropX = cropBounds?.x ?? 0;
    const cropY = cropBounds?.y ?? 0;
    const cropW = cropBounds?.width ?? width;
    const cropH = cropBounds?.height ?? height;

    // eslint-disable-next-line no-console
    console.log('[chartSnapshot] start', { width, height, cropBounds, cropW, cropH, imageCount: svgElement.querySelectorAll('image').length });

    if (cropW <= 0 || cropH <= 0) {
      // eslint-disable-next-line no-console
      console.warn('[chartSnapshot] invalid crop size, aborting');
      resolve(null);
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(cropW * scale));
      canvas.height = Math.max(1, Math.round(cropH * scale));
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      const resolvedBackground = backgroundColor || '#fafbfc';
      ctx.fillStyle = resolvedBackground;
      ctx.fillRect(0, 0, cropW, cropH);

      // Pre-load all game images referenced by the SVG so they can be drawn
      // (with their circular clip-paths) directly onto the canvas.
      // The images are fetched directly so the Authorization header is included,
      // and the resulting blob is loaded as a same-origin URL (no canvas taint).
      const imageElements = svgElement.querySelectorAll('image');

      const loadImages = Array.from(imageElements).map((imageEl) => new Promise(async (imgResolve) => {
        try {
          const href = imageEl.getAttribute('href') || imageEl.getAttribute('xlink:href');
          const x = parseFloat(imageEl.getAttribute('x'));
          const y = parseFloat(imageEl.getAttribute('y'));
          const imgWidth = parseFloat(imageEl.getAttribute('width'));
          const imgHeight = parseFloat(imageEl.getAttribute('height'));
          const clipPathId = imageEl.getAttribute('clip-path');

          if (!href || isNaN(x) || isNaN(y) || isNaN(imgWidth) || isNaN(imgHeight)) {
            imgResolve(null);
            return;
          }

          const isApiUrl = href.startsWith(API_BASE);
          const token = isApiUrl ? authService.getToken() : null;
          const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
          const credentials = isApiUrl ? 'include' : 'omit';

          const res = await fetch(href, { headers: authHeaders, credentials });
          if (!res.ok) {
            logger.warn('Chart image fetch failed:', res.status, href);
            imgResolve(null);
            return;
          }
          const blob = await res.blob();
          const objectUrl = URL.createObjectURL(blob);

          const gameImg = new Image();
          const timeout = setTimeout(() => {
            URL.revokeObjectURL(objectUrl);
            imgResolve(null);
          }, 8000);
          gameImg.onload = () => {
            clearTimeout(timeout);
            imgResolve({ img: gameImg, x, y, width: imgWidth, height: imgHeight, clipPathId, objectUrl });
          };
          gameImg.onerror = () => {
            clearTimeout(timeout);
            URL.revokeObjectURL(objectUrl);
            imgResolve(null); // Continue even if a single image fails
          };
          gameImg.src = objectUrl;
        } catch (imgErr) {
          logger.warn('Failed to process chart image:', imgErr);
          imgResolve(null);
        }
      }));

      Promise.all(loadImages).then((loadedImages) => {
        const usableImages = loadedImages.filter(Boolean);

        // Clone the SVG without <image> children so the background/bubbles
        // render cleanly (cross-origin images inside an <svg> block canvas
        // rasterization of the whole tree).
        const svgClone = svgElement.cloneNode(true);
        svgClone.querySelectorAll('image').forEach((img) => img.remove());

        // SVGs rendered in an <img> context don't reliably resolve CSS
        // variables, so replace var(...) fills/strokes with concrete colors.
        const colorMap = {
          '--bg-primary': '#ffffff',
          '--bg-secondary': '#fafbfc',
          '--pista-green-primary': '#2E7D32',
          '--pista-green-secondary': '#66BB6A',
          '--pista-green-hover': '#388E3C',
          '--border-color': '#dddddd',
          '--text-primary': '#333333',
          '--text-secondary': '#666666',
          '--pista-button-text': '#ffffff',
        };

        svgClone.querySelectorAll('*').forEach((el) => {
          ['fill', 'stroke'].forEach((attr) => {
            const value = el.getAttribute(attr);
            if (value && value.startsWith('var(')) {
              const match = value.match(/var\(--([^),]+)(?:,\s*([^)]*))?\)/);
              if (match) {
                const varName = `--${match[1]}`;
                const fallback = match[2] ? match[2].trim() : '';
                el.setAttribute(attr, colorMap[varName] || fallback || 'none');
              }
            }
          });
        });

        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgUrl = 'data:image/svg+xml;base64,' + utf8ToBase64(svgData);

        const img = new Image();

        img.onload = () => {
          // eslint-disable-next-line no-console
          console.log('[chartSnapshot] svg img loaded', img.naturalWidth, img.naturalHeight, canvas.width, canvas.height, 'usableImages', usableImages.length);
          // Draw the full SVG at the natural size, offset so the cropBounds
          // rectangle lines up with the top-left of the canvas.
          ctx.drawImage(img, -cropX, -cropY, img.naturalWidth, img.naturalHeight);

          usableImages.forEach(({ img: gameImg, x, y, width: w, height: h, clipPathId }) => {
            try {
              if (clipPathId) {
                const rawId = clipPathId.replace(/^url\((['"]?)(#?)(.*?)\1\)$/, '$3');
                const clipPathEl = svgElement.querySelector(`#${rawId}`);
                const circleEl = clipPathEl?.querySelector('circle');
                if (circleEl) {
                  const cx = parseFloat(circleEl.getAttribute('cx')) - cropX;
                  const cy = parseFloat(circleEl.getAttribute('cy')) - cropY;
                  const r = parseFloat(circleEl.getAttribute('r'));
                  ctx.save();
                  ctx.beginPath();
                  ctx.arc(cx, cy, r, 0, 2 * Math.PI);
                  ctx.clip();
                  ctx.drawImage(gameImg, x - cropX, y - cropY, w, h);
                  ctx.restore();
                  return;
                }
              }
              ctx.drawImage(gameImg, x - cropX, y - cropY, w, h);
            } catch (drawErr) {
              logger.warn('Failed to draw chart image:', drawErr);
            }
          });

          canvas.toBlob((blob) => {
            usableImages.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
            // eslint-disable-next-line no-console
            console.log('[chartSnapshot] toBlob', blob?.size);
            resolve(blob && blob.size > 0 ? new File([blob], filename, { type: 'image/png' }) : null);
          }, 'image/png');
        };

        img.onerror = (err) => {
          // eslint-disable-next-line no-console
          console.error('[chartSnapshot] svg img onerror', err);
          resolve(null);
        };

        img.src = svgUrl;
      });
    } catch (error) {
      logger.error('Error creating chart snapshot:', error);
      resolve(null);
    }
  });
}
